"use client";

import { type ReactNode, useState } from "react";
import {
  Notice,
  Panel,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  computeView,
  EMPTY_FIELDS,
  EXAMPLE_KEYS,
  type FieldKey,
  type Fields,
  type Mode,
  type Source,
} from "@/components/tools/stats-descriptive/standard-error-views";
import {
  Explanation,
  Formula,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import { formatStat } from "@/lib/tool-kit/stats-descriptive/format";

/** English strings. A translated content file overrides these through `ui`. */
export const STANDARD_ERROR_CALCULATOR_STRINGS = {
  modeLabel: "Standard error of",
  modeMean: "A mean",
  modeProportion: "A proportion",
  modeTwoMeans: "The difference between two means",
  modeTwoProportions: "The difference between two proportions",
  sourceLabel: "Enter",
  sourceSummary: "The SD and n",
  sourceData: "Raw data",
  sdLabel: "Standard deviation (s)",
  nLabel: "Sample size (n)",
  dataLabel: "Your data — numbers separated by commas, spaces, or new lines",
  dataPlaceholder: "12.1, 14.3, 9.8, 11.5, 13.2",
  valuesDetected: "one: value detected|other: values detected",
  proportionLabel: "Sample proportion p̂ — 0.45, 45% or 54/120",
  group1: "Group 1",
  group2: "Group 2",
  meanOptional: "Mean (optional)",
  tryExample: "Try an example",
  clear: "Clear",
  exampleS: "12.4",
  exampleN: "36",
  exampleData: "12.1, 14.3, 9.8, 11.5, 13.2, 10.9, 12.7, 15.0",
  exampleP: "54/120",
  exampleNp: "120",
  exampleM1: "78.2",
  exampleS1: "10.5",
  exampleN1: "40",
  exampleM2: "72.6",
  exampleS2: "12.1",
  exampleN2: "35",
  exampleP1: "0.62",
  exampleNp1: "150",
  exampleP2: "0.48",
  exampleNp2: "140",
  enterPrompt:
    "Fill in every field above — sample sizes must be whole numbers of 2 or more, and proportions between 0 and 1.",
  results: "Results",
  standardError: "Standard error",
  sampleMean: "Mean (x̄)",
  sampleSd: "Sample SD (s)",
  sampleSize: "n",
  proportion: "p̂",
  difference: "Difference",
  unpooled: "Unpooled SE (Welch)",
  pooled: "Pooled SE (equal variances)",
  pooledSd: "Pooled SD (sₚ)",
  unpooledProportion: "Unpooled SE (for an interval)",
  pooledProportion: "Pooled SE (for the z-test)",
  pooledP: "Pooled proportion (p̄)",
  workingTitle: "How it was calculated",
  meanWorking: "SE = s ÷ √n = {s} ÷ √{n} = {s} ÷ {root} = {se}",
  rawMeanWorking: "x̄ = Σx ÷ n = {sum} ÷ {n} = {mean}",
  rawSdWorking: "s = √(Σ(x − x̄)² ÷ (n − 1)) = √({ss} ÷ {df}) = {s}",
  proportionWorking:
    "SE = √(p̂(1 − p̂) ÷ n) = √({p} × {q} ÷ {n}) = √{inner} = {se}",
  welchWorking:
    "SE = √(s₁² ÷ n₁ + s₂² ÷ n₂) = √({s1}² ÷ {n1} + {s2}² ÷ {n2}) = √({a} + {b}) = {se}",
  pooledSdWorking:
    "sₚ = √(((n₁ − 1)s₁² + (n₂ − 1)s₂²) ÷ (n₁ + n₂ − 2)) = √(({n1m} × {v1} + {n2m} × {v2}) ÷ {df}) = {sp}",
  pooledWorking:
    "SE = sₚ × √(1 ÷ n₁ + 1 ÷ n₂) = {sp} × √(1 ÷ {n1} + 1 ÷ {n2}) = {se}",
  differenceWorking: "x̄₁ − x̄₂ = {m1} − {m2} = {diff}",
  propUnpooledWorking:
    "SE = √(p̂₁(1 − p̂₁) ÷ n₁ + p̂₂(1 − p̂₂) ÷ n₂) = √({a} + {b}) = {se}",
  pooledPWorking:
    "p̄ = (p̂₁n₁ + p̂₂n₂) ÷ (n₁ + n₂) = ({x1} + {x2}) ÷ {nn} = {pbar}",
  propPooledWorking:
    "SE = √(p̄(1 − p̄)(1 ÷ n₁ + 1 ÷ n₂)) = √({pbar} × {qbar} × {sumInv}) = {se}",
  propDifferenceWorking: "p̂₁ − p̂₂ = {p1} − {p2} = {diff}",
  meanNote:
    "The SE shrinks with the square root of n: four times the sample halves it.",
  welchNote:
    "Use the unpooled SE unless you have a reason to assume equal variances; the pooled SE is the one Student's t-test uses.",
  proportionNote:
    "The unpooled SE goes into a confidence interval for the difference; the pooled SE, computed as if the two proportions were equal, goes into the z-test.",
} as const;

type Strings = typeof STANDARD_ERROR_CALCULATOR_STRINGS;

function GroupFields({
  index,
  fields,
  update,
  strings,
  proportions,
}: {
  index: 1 | 2;
  fields: Fields;
  update: (key: FieldKey) => (value: string) => void;
  strings: Strings;
  proportions: boolean;
}) {
  const key = (name: string) => `${name}${index}` as FieldKey;
  if (proportions) {
    return (
      <Panel title={index === 1 ? strings.group1 : strings.group2}>
        <TextField
          id={`se-p${index}`}
          label={strings.proportionLabel}
          onChange={update(key("p"))}
          value={fields[key("p")]}
        />
        <TextField
          id={`se-np${index}`}
          label={strings.nLabel}
          numeric
          onChange={update(key("np"))}
          value={fields[key("np")]}
        />
      </Panel>
    );
  }
  return (
    <Panel title={index === 1 ? strings.group1 : strings.group2}>
      <TextField
        id={`se-m${index}`}
        label={strings.meanOptional}
        numeric
        onChange={update(key("m"))}
        value={fields[key("m")]}
      />
      <TextField
        id={`se-s${index}`}
        label={strings.sdLabel}
        numeric
        onChange={update(key("s"))}
        value={fields[key("s")]}
      />
      <TextField
        id={`se-n${index}`}
        label={strings.nLabel}
        numeric
        onChange={update(key("n"))}
        value={fields[key("n")]}
      />
    </Panel>
  );
}

function SingleFields({
  mode,
  source,
  setSource,
  fields,
  update,
  strings,
  hint,
}: {
  mode: Mode;
  source: Source;
  setSource: (value: Source) => void;
  fields: Fields;
  update: (key: FieldKey) => (value: string) => void;
  strings: Strings;
  hint: ReactNode;
}) {
  if (mode === "proportion") {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="se-p"
          label={strings.proportionLabel}
          onChange={update("p")}
          value={fields.p}
        />
        <TextField
          id="se-np"
          label={strings.nLabel}
          numeric
          onChange={update("np")}
          value={fields.np}
        />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <PillGroup
        label={strings.sourceLabel}
        name="se-source"
        onChange={setSource}
        options={[
          { value: "summary", label: strings.sourceSummary },
          { value: "data", label: strings.sourceData },
        ]}
        value={source}
      />
      {source === "data" ? (
        <TextAreaField
          hint={hint}
          id="se-data"
          label={strings.dataLabel}
          onChange={update("data")}
          placeholder={strings.dataPlaceholder}
          value={fields.data}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="se-s"
            label={strings.sdLabel}
            numeric
            onChange={update("s")}
            value={fields.s}
          />
          <TextField
            id="se-n"
            label={strings.nLabel}
            numeric
            onChange={update("n")}
            value={fields.n}
          />
        </div>
      )}
    </div>
  );
}

export default function StandardErrorCalculatorWidget() {
  const strings = useToolUi(STANDARD_ERROR_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("mean");
  const [source, setSource] = useState<Source>("summary");
  const [fields, setFields] = useState<Fields>(EMPTY_FIELDS);

  const num = (value: number) => formatStat(fmt, value);
  const update = (key: FieldKey) => (value: string) =>
    setFields((previous) => ({ ...previous, [key]: value }));
  const view = computeView(mode, source, fields, strings, num);
  const dataCount = parseNumberList(fields.data).length;
  const twoGroups = mode === "twoMeans" || mode === "twoProportions";

  function fillExample() {
    const example = { ...EMPTY_FIELDS };
    for (const key of Object.keys(EXAMPLE_KEYS) as FieldKey[]) {
      example[key] = strings[EXAMPLE_KEYS[key]];
    }
    setFields(example);
  }

  return (
    <>
      <section className="space-y-4">
        <PillGroup
          label={strings.modeLabel}
          name="se-mode"
          onChange={setMode}
          options={[
            { value: "mean", label: strings.modeMean },
            { value: "proportion", label: strings.modeProportion },
            { value: "twoMeans", label: strings.modeTwoMeans },
            { value: "twoProportions", label: strings.modeTwoProportions },
          ]}
          value={mode}
        />
        {twoGroups ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {([1, 2] as const).map((index) => (
              <GroupFields
                fields={fields}
                index={index}
                key={index}
                proportions={mode === "twoProportions"}
                strings={strings}
                update={update}
              />
            ))}
          </div>
        ) : (
          <SingleFields
            fields={fields}
            hint={
              <>
                {fmt(dataCount)}
                {tPlural(strings.valuesDetected, locale, dataCount)}
              </>
            }
            mode={mode}
            setSource={setSource}
            source={source}
            strings={strings}
            update={update}
          />
        )}
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={fillExample}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setFields(EMPTY_FIELDS)}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {view ? (
        <section className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite">
            <StatGrid>
              {view.cards.map((card) => (
                <StatCard
                  emphasis={card.emphasis}
                  key={card.label}
                  label={card.label}
                  value={card.value}
                />
              ))}
            </StatGrid>
          </div>
          <WorkingPanel title={strings.workingTitle}>
            {view.working.map((line) => (
              <Formula key={line}>{line}</Formula>
            ))}
            <Explanation>{view.note}</Explanation>
          </WorkingPanel>
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </>
  );
}
